import React, { memo, useState, useEffect, useCallback } from 'react';
import { Text, View, StyleSheet, Dimensions } from 'react-native';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../../constants/styles';
import { useDidUpdateEffect } from '../../../hooks';

import { I18n, Utils, CTAButton, Container, Navigation } from '../../components';

import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../../actions/statusTypes';
import { EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY } from '../../../constants/eventTypes';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { resetState } from './slice';
import {
  getAppointmentSlotAvailability,
  resetStatus as resetSchedulerStatus,
} from '../scheduler/slice';

import Cal from '../scheduler/cal_patient';
import { appointmentLabelForType } from '../scheduler/common_views';

const PHARequestForm = memo((props) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDateIsAvailable, setSelectedDateIsAvailable] = useState(false);

  const fetchData = useCallback(() => {
    props.actions.getAppointmentSlotAvailability({
      employerKey: props.base,
      eventType: props.eventType,
    });
  }, [props.base, props.eventType]);
  useEffect(() => {
    if (props.base && props.eventType) {
      fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    Utils.trackScreen('Auth', 'PHARequestForm');

    props.actions.resetState();
    props.actions.resetSchedulerStatus();
  }, []);

  useDidUpdateEffect(() => {
    if (props.aptsRequestStatus === SUCCESS || props.aptsRequestStatus === FAILED) {
      fetchData();
    }
  }, [props.aptsRequestStatus]);

  const handleDateSelect = useCallback((selectedDate) => {
    setSelectedDate(selectedDate);
  }, []);
  const handleTimesChange = useCallback((selectedTimes) => {
    const selectedTime = selectedTimes.length ? selectedTimes[0] : null;
    setSelectedTime(selectedTime);
  }, []);

  const _openDetailsForm = useCallback(
    (selectedTimeOverride) => {
      Navigation.showModal({
        screen: 'PHARequestFormDetailsAuth',
        title: 'Patient Info',
        passProps: {
          selectedDate,
          selectedTime: selectedTimeOverride || selectedTime,
          eventType: props.eventType,
          base: props.base,
          userData: props.userData,
        },
      });
    },
    [selectedDate, selectedTime, props.eventType, props.base]
  );

  const openDetailsForm = useCallback(() => {
    _openDetailsForm(null);
  }, [_openDetailsForm]);

  const openDetailsFormCMR = useCallback(() => {
    _openDetailsForm('07:30');
  }, [_openDetailsForm]);

  const { width } = Dimensions.get('window');
  const calWidth = Math.min(500, width - 20);
  return (
    <Container
      errorMessage={props.errorMessage || props.aptsSlotAvailabilityError?.message}
      renderActivityOverlay={
        props.baseInfoStatus === REQUESTING || props.aptsSlotAvailabilityStatus === REQUESTING
      }
      scrollView={false}
      style={styles.container}
    >
      <View style={styles.paneWrapper}>
        <View style={[{ alignItems: 'center' }]}>
          <Text style={styles.contentTitle}>
            {`${appointmentLabelForType(props.eventType)} Appointment`.toUpperCase()}
          </Text>

          <View style={{ width: calWidth }}>
            {!!props.infoLoaded && !!props.aptsSlotAvailabilityData?.available && (
              <Cal
                onSelectedDate={handleDateSelect}
                onSelectedDateIsAvailable={setSelectedDateIsAvailable}
                onTimesChange={handleTimesChange}
                events={null}
                slotAvailability={props.aptsSlotAvailabilityData}
                aptsSlotAvailabilityStatus={props.aptsSlotAvailabilityStatus}
                eventType={props.eventType}
                calWidth={calWidth}
                base={props.base}
                timeZone={props.timeZone}
                eventTypesConfig={props.eventTypesConfig}
              />
            )}
          </View>
        </View>

        {![EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(props.eventType) ? (
          <View style={[{ alignItems: 'center' }]}>
            <CTAButton
              containerStyle={{ zIndex: -2 }}
              style={{ width: 200 }}
              onPress={openDetailsForm}
              label={I18n.t('auth.requestTime')}
              disabled={!props.infoLoaded || !selectedTime}
              testID="requestTimeButton"
            />
          </View>
        ) : (
          <View style={[{ alignItems: 'center' }]}>
            <View style={[{ flexDirection: 'row', width: calWidth, paddingTop: 25 }]}>
              <CTAButton
                styleType="cancel"
                containerStyle={{ zIndex: -2 }}
                style={{ width: 130 }}
                onPress={props.back}
                label={'← Back'}
                disabled={false}
              />
              <View style={{ flex: 1 }} />
              <CTAButton
                containerStyle={{ zIndex: -2 }}
                style={{ width: 200 }}
                onPress={openDetailsFormCMR}
                label="Request Date"
                disabled={!props.infoLoaded || !selectedDateIsAvailable}
                testID="requestTimeButton"
              />
            </View>
          </View>
        )}
      </View>
    </Container>
  );
});
PHARequestForm.displayName = 'PHARequestForm';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  paneWrapper: {
    flex: 1,
    marginVertical: 20,
  },
  contentTitle: {
    ...FONT.medium,
    color: COLOR.text,
    fontSize: FONT_SIZE.huge,
    marginBottom: 15,
    textAlign: 'center',
    letterSpacing: 3,
  },
});

function mapStateToProps(state, ownProps) {
  return {
    baseInfoStatus: state.screens.baseUnauthed.baseInfoStatus,
    infoLoaded: state.screens.baseUnauthed.baseInfoStatus === SUCCESS,
    aptsSlotAvailabilityData: state.screens.scheduler.aptsSlotAvailabilityData,
    aptsSlotAvailabilityStatus: state.screens.scheduler.aptsSlotAvailabilityStatus,
    aptsSlotAvailabilityError: state.screens.scheduler.aptsSlotAvailabilityError,
    aptsRequestStatus: state.screens.scheduler.aptsRequestStatus,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { resetState, resetSchedulerStatus, getAppointmentSlotAvailability },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PHARequestForm);
